import React, { ErrorInfo, ReactNode } from 'react';
import { RUMHelpers } from 'services/observability/datadog';
import { Product, Team } from 'teams';
import { ErrorMarkup } from './ErrorMarkup';
import { ERROR_BOUNDARY } from './constants';

export interface ErrorBoundaryProps {
  /** Team that owns the wrapped components */
  teamName: Team;
  /** Product name for the wrapped components */
  productName: Product;
  /** Markup to display in case of an error */
  errorMarkup?: ReactNode;
  /** Callback function called after an error */
  errorHandler?: (error: Error, errorInfo: ErrorInfo) => any;
  /** Additional, context to send to datadog. In, case of an error. */
  errorContext?: Record<string, unknown>;
  children: ReactNode | undefined;
}

type ErrorBoundaryState = {
  /** Error state */
  hasError: boolean;
};

class ReactErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = {
    hasError: false,
  };

  static defaultProps = {
    errorMarkup: <ErrorMarkup />,
    errorContext: {},
    teamName: '',
    productName: '',
  };

  static getDerivedStateFromError() {
    // Update state so the next render will show the error markup
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    const { errorHandler, errorContext, teamName, productName } = this.props;
    const componentError = new Error(error.message);
    componentError.name = `React ErrorBoundary ${error.toString()}`;
    componentError.stack = errorInfo?.componentStack || undefined;

    // Error callback function
    if (errorHandler) {
      errorHandler(error, errorInfo);
    }

    RUMHelpers.addRUMError(
      componentError,
      {
        name: ERROR_BOUNDARY,
        teamName,
        productName,
      },
      {
        ...errorContext,
      },
    );
  }

  render() {
    const { hasError } = this.state;
    const { errorMarkup, children } = this.props;

    return hasError ? errorMarkup : children;
  }
}

export const ErrorBoundary = ReactErrorBoundary;
